import type { QueryClient } from '@tanstack/react-query'
import { createRootRouteWithContext, createRoute, createRouter, lazyRouteComponent, Outlet, redirect } from '@tanstack/react-router'
import { FullPageLoader } from '@/components/full-page-loader'
import { Toaster } from '@/components/ui/sonner'
import { meQueryOptions } from '@/features/auth/api'
import { InvitePage } from '@/features/auth/routes/invite'
import { LoginPage } from '@/features/auth/routes/login'
import { RegisterPage } from '@/features/auth/routes/register'
import { DashboardPage } from '@/features/dashboard/routes/dashboard-page'
import { LocationsPage } from '@/features/locations/routes/locations-page'
import { ComingSoonPage } from '@/features/placeholder/routes/coming-soon'
import { NotFoundPage, RouteErrorPage } from '@/features/placeholder/routes/not-found'
import { validateDisplaysSearch } from '@/features/displays/lib/search'
import { validateMediaSearch } from '@/features/media/lib/search'
import { validatePreviewSearch, validateSlideshowsSearch } from '@/features/slideshows/lib/search'
import { validateChannelEditorSearch } from '@/features/channels/lib/search'
import { validateCalendarSearch } from '@/features/calendar/lib/search'
import { validateSchedulesSearch } from '@/features/schedules/lib/search'
import { validateAnalyticsSearch, validateProofOfPlaySearch } from '@/features/analytics/lib/search'
import { validateVariablesSearch } from '@/features/variables/lib/search'
import { validateFlowSearch, validateFlowsSearch, validateNewFlowSearch } from '@/features/flows/lib/search'
import { validateLocalContentSearch } from '@/features/content-slots/lib/search'
import { validateFieldsSearch } from '@/features/template-fields/lib/scope'
import { AppLayout } from '@/features/shell/app-layout'
import { useApplyTheme } from '@/hooks/use-theme'


/* Code-split route components (admin area is its own bundle). */
const AdminLayout = lazyRouteComponent(() => import('@/features/admin/admin-layout'), 'AdminLayout')
const AdminAuditLogsPage = lazyRouteComponent(() => import('@/features/admin/routes/admin-audit-logs-page'), 'AdminAuditLogsPage')
const AdminDashboardPage = lazyRouteComponent(() => import('@/features/admin/routes/admin-dashboard-page'), 'AdminDashboardPage')
const AdminPlansPage = lazyRouteComponent(() => import('@/features/admin/routes/admin-plans-page'), 'AdminPlansPage')
const AdminTenantDetailPage = lazyRouteComponent(() => import('@/features/admin/routes/admin-tenant-detail-page'), 'AdminTenantDetailPage')
const AdminTenantsPage = lazyRouteComponent(() => import('@/features/admin/routes/admin-tenants-page'), 'AdminTenantsPage')
const AdminReleasesPage = lazyRouteComponent(() => import('@/features/admin/routes/admin-releases-page'), 'AdminReleasesPage')
const AdminReleaseDetailPage = lazyRouteComponent(() => import('@/features/admin/routes/admin-release-detail-page'), 'AdminReleaseDetailPage')
const AdminHealthPage = lazyRouteComponent(() => import('@/features/admin/routes/admin-health-page'), 'AdminHealthPage')
const AdminSecurityPage = lazyRouteComponent(() => import('@/features/admin/routes/admin-security-page'), 'AdminSecurityPage')
const AdminFailedJobsPage = lazyRouteComponent(() => import('@/features/admin/routes/admin-failed-jobs-page'), 'AdminFailedJobsPage')
const AuditLogPage = lazyRouteComponent(() => import('@/features/audit/routes/audit-log-page'), 'AuditLogPage')
const LocationDetailPage = lazyRouteComponent(() => import('@/features/locations/routes/location-detail-page'), 'LocationDetailPage')
const OnboardingPage = lazyRouteComponent(() => import('@/features/onboarding/routes/onboarding-page'), 'OnboardingPage')
const RolesPage = lazyRouteComponent(() => import('@/features/roles/routes/roles-page'), 'RolesPage')
const OrganizationSettings = lazyRouteComponent(() => import('@/features/settings/routes/organization-settings'), 'OrganizationSettings')
const ProfileSettings = lazyRouteComponent(() => import('@/features/settings/routes/profile-settings'), 'ProfileSettings')
const SecuritySettings = lazyRouteComponent(() => import('@/features/settings/routes/security-settings'), 'SecuritySettings')
const NotificationsPage = lazyRouteComponent(() => import('@/features/notifications/routes/notifications-page'), 'NotificationsPage')
const NotificationSettings = lazyRouteComponent(() => import('@/features/notifications/routes/notification-settings'), 'NotificationSettings')
const WebhooksSettings = lazyRouteComponent(() => import('@/features/integrations/routes/webhooks-settings'), 'WebhooksSettings')
const ApiKeysSettings = lazyRouteComponent(() => import('@/features/integrations/routes/api-keys-settings'), 'ApiKeysSettings')
const BrandingSettings = lazyRouteComponent(() => import('@/features/integrations/routes/branding-settings'), 'BrandingSettings')
const SubscriptionSettings = lazyRouteComponent(() => import('@/features/settings/routes/subscription-settings'), 'SubscriptionSettings')
const SettingsLayout = lazyRouteComponent(() => import('@/features/settings/routes/settings-layout'), 'SettingsLayout')
const PlayerUpdateSettings = lazyRouteComponent(() => import('@/features/settings/routes/player-update-settings'), 'PlayerUpdateSettings')
const DisplaysPage = lazyRouteComponent(() => import('@/features/displays/routes/displays-page'), 'DisplaysPage')
const DisplayDetailPage = lazyRouteComponent(() => import('@/features/displays/routes/display-detail-page'), 'DisplayDetailPage')
const PlayersPage = lazyRouteComponent(() => import('@/features/players/routes/players-page'), 'PlayersPage')
const PlayerDetailPage = lazyRouteComponent(() => import('@/features/players/routes/player-detail-page'), 'PlayerDetailPage')
const GroupsPage = lazyRouteComponent(() => import('@/features/groups/routes/groups-page'), 'GroupsPage')
const PairPage = lazyRouteComponent(() => import('@/features/pairing/routes/pair-page'), 'PairPage')
const SlideshowsPage = lazyRouteComponent(() => import('@/features/slideshows/routes/slideshows-page'), 'SlideshowsPage')
const SlideshowEditorPage = lazyRouteComponent(() => import('@/features/slideshows/routes/slideshow-editor-page'), 'SlideshowEditorPage')
const SlideshowPreviewPage = lazyRouteComponent(() => import('@/features/slideshows/routes/slideshow-preview-page'), 'SlideshowPreviewPage')
const ChannelsPage = lazyRouteComponent(() => import('@/features/channels/routes/channels-page'), 'ChannelsPage')
const ChannelEditorPage = lazyRouteComponent(() => import('@/features/channels/routes/channel-editor-page'), 'ChannelEditorPage')
const DataSourcesPage = lazyRouteComponent(() => import('@/features/data-sources/routes/data-sources-page'), 'DataSourcesPage')
const DataSourceDetailPage = lazyRouteComponent(() => import('@/features/data-sources/routes/data-source-detail-page'), 'DataSourceDetailPage')
const VariablesPage = lazyRouteComponent(() => import('@/features/variables/routes/variables-page'), 'VariablesPage')
const RulesPage = lazyRouteComponent(() => import('@/features/rules/routes/rules-page'), 'RulesPage')
const FlowsPage = lazyRouteComponent(() => import('@/features/flows/routes/flows-page'), 'FlowsPage')
const NewFlowPage = lazyRouteComponent(() => import('@/features/flows/routes/flow-editor-page'), 'NewFlowPage')
const FlowDetailPage = lazyRouteComponent(() => import('@/features/flows/routes/flow-editor-page'), 'FlowDetailPage')
const LocalContentPage = lazyRouteComponent(() => import('@/features/content-slots/routes/local-content-page'), 'LocalContentPage')
const SlotApprovalsPage = lazyRouteComponent(() => import('@/features/content-slots/routes/slot-approvals-page'), 'SlotApprovalsPage')
const FieldsPage = lazyRouteComponent(() => import('@/features/template-fields/routes/fields-page'), 'FieldsPage')
const AnalyticsPage = lazyRouteComponent(() => import('@/features/analytics/routes/analytics-page'), 'AnalyticsPage')
const ProofOfPlayPage = lazyRouteComponent(() => import('@/features/analytics/routes/proof-of-play-page'), 'ProofOfPlayPage')
const CampaignsPage = lazyRouteComponent(() => import('@/features/campaigns/routes/campaigns-page'), 'CampaignsPage')
const CampaignDetailPage = lazyRouteComponent(() => import('@/features/campaigns/routes/campaign-detail-page'), 'CampaignDetailPage')
const SchedulesPage = lazyRouteComponent(() => import('@/features/schedules/routes/schedules-page'), 'SchedulesPage')
const CalendarPage = lazyRouteComponent(() => import('@/features/calendar/routes/calendar-page'), 'CalendarPage')
const EmergencyPage = lazyRouteComponent(() => import('@/features/emergency/routes/emergency-page'), 'EmergencyPage')
const MediaPage = lazyRouteComponent(() => import('@/features/media/routes/media-page'), 'MediaPage')
const UsersPage = lazyRouteComponent(() => import('@/features/users/routes/users-page'), 'UsersPage')
const NewRolePage = lazyRouteComponent(() => import('@/features/roles/routes/role-editor-page'), 'NewRolePage')
const EditRolePage = lazyRouteComponent(() => import('@/features/roles/routes/role-editor-page'), 'EditRolePage')

export interface RouterContext {
  queryClient: QueryClient
}

/* ----------------------------------------------------------- search parsers */

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.length > 0 ? v : undefined)
const bool = (v: unknown): true | undefined => (v === true || v === 'true' || v === 1 || v === '1' ? true : undefined)

/* -------------------------------------------------------------------- root */

function RootComponent() {
  useApplyTheme()
  return (
    <>
      <Outlet />
      <Toaster />
    </>
  )
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFoundPage,
  errorComponent: ({ error, reset }) => <RouteErrorPage error={error} reset={reset} />,
})

/* ------------------------------------------------------------ public routes */

/** Signed-in users visiting /login or /register go straight to the app. */
async function redirectIfAuthenticated({ context, search }: { context: RouterContext; search: { redirect?: string } }) {
  const me = await context.queryClient.ensureQueryData(meQueryOptions).catch(() => null)
  if (me) throw redirect({ href: search.redirect && search.redirect.startsWith('/') && !search.redirect.startsWith('//') ? search.redirect : '/' })
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'login',
  validateSearch: (s: Record<string, unknown>): { redirect?: string } => ({ redirect: str(s.redirect) }),
  beforeLoad: ({ context, search }) => redirectIfAuthenticated({ context, search }),
  component: LoginPage,
})

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'register',
  validateSearch: (s: Record<string, unknown>): { redirect?: string; email?: string } => ({ redirect: str(s.redirect), email: str(s.email) }),
  beforeLoad: ({ context, search }) => redirectIfAuthenticated({ context, search }),
  component: RegisterPage,
})

const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'invite/$token',
  component: InvitePage,
})

/* ------------------------------------------------------- authenticated area */

const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_authed',
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions)
    if (!me) throw redirect({ to: '/login', search: { redirect: location.href } })
    return { me }
  },
  pendingComponent: () => <FullPageLoader />,
  component: Outlet,
})

const onboardingRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'onboarding',
  component: OnboardingPage,
})

/**
 * Tenant app: requires a current tenant. Platform admins without any organization go to
 * the admin console; everyone else is sent to onboarding to create one.
 */
const appRoute = createRoute({
  getParentRoute: () => authedRoute,
  id: '_app',
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions)
    if (!me?.current_tenant) {
      if (me?.user.is_platform_admin && me.memberships.length === 0) throw redirect({ to: '/admin' })
      throw redirect({ to: '/onboarding' })
    }
  },
  component: AppLayout,
})

const dashboardRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: DashboardPage })

const locationsRoute = createRoute({ getParentRoute: () => appRoute, path: 'locations', component: LocationsPage })
const locationDetailRoute = createRoute({ getParentRoute: () => appRoute, path: 'locations/$locationId', component: LocationDetailPage })

const displaysRoute = createRoute({ getParentRoute: () => appRoute, path: 'displays', validateSearch: validateDisplaysSearch, component: DisplaysPage })
const displayDetailRoute = createRoute({ getParentRoute: () => appRoute, path: 'displays/$displayId', component: DisplayDetailPage })
const playersRoute = createRoute({ getParentRoute: () => appRoute, path: 'players', component: PlayersPage })
const playerDetailRoute = createRoute({ getParentRoute: () => appRoute, path: 'players/$playerId', component: PlayerDetailPage })
const groupsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'groups',
  validateSearch: (s: Record<string, unknown>): { tab?: 'tags' } => ({ tab: s.tab === 'tags' ? 'tags' : undefined }),
  component: GroupsPage,
})
/** Media library. `?media=<id>` opens the detail drawer (deep link). */
const mediaRoute = createRoute({ getParentRoute: () => appRoute, path: 'media', validateSearch: validateMediaSearch, component: MediaPage })

/** Slideshows: list → structured editor → preview player (`?version=` plays a published version). */
const slideshowsRoute = createRoute({ getParentRoute: () => appRoute, path: 'slideshows', validateSearch: validateSlideshowsSearch, component: SlideshowsPage })
const slideshowEditorRoute = createRoute({ getParentRoute: () => appRoute, path: 'slideshows/$slideshowId', component: SlideshowEditorPage })
const slideshowPreviewRoute = createRoute({ getParentRoute: () => appRoute, path: 'slideshows/$slideshowId/preview', validateSearch: validatePreviewSearch, component: SlideshowPreviewPage })

/** Channels: list → editor (base items, layer blocks, preview for display). */
const channelsRoute = createRoute({ getParentRoute: () => appRoute, path: 'channels', component: ChannelsPage })
const channelEditorRoute = createRoute({ getParentRoute: () => appRoute, path: 'channels/$channelId', validateSearch: validateChannelEditorSearch, component: ChannelEditorPage })

/** Phase 11: data sources (weather, feeds, APIs) bound by slideshows. */
const dataSourcesRoute = createRoute({ getParentRoute: () => appRoute, path: 'data-sources', component: DataSourcesPage })
const dataSourceDetailRoute = createRoute({ getParentRoute: () => appRoute, path: 'data-sources/$dataSourceId', component: DataSourceDetailPage })

/** Phase B: variables — global definitions/values, per-scope overrides (`?tab=&scope=`). */
const variablesRoute = createRoute({ getParentRoute: () => appRoute, path: 'variables', validateSearch: validateVariablesSearch, component: VariablesPage })

/** Phase E: content slots (local content + approvals) and template fields. */
const localContentRoute = createRoute({ getParentRoute: () => appRoute, path: 'local-content', validateSearch: validateLocalContentSearch, component: LocalContentPage })
const slotApprovalsRoute = createRoute({ getParentRoute: () => appRoute, path: 'approvals/slots', component: SlotApprovalsPage })
const fieldsRoute = createRoute({ getParentRoute: () => appRoute, path: 'fields', validateSearch: validateFieldsSearch, component: FieldsPage })

/** Phase 12: analytics overview and proof-of-play report (`?range=&location=&display=` …). */
const analyticsRoute = createRoute({ getParentRoute: () => appRoute, path: 'analytics', validateSearch: validateAnalyticsSearch, component: AnalyticsPage })
const proofOfPlayRoute = createRoute({ getParentRoute: () => appRoute, path: 'analytics/proof-of-play', validateSearch: validateProofOfPlaySearch, component: ProofOfPlayPage })

/** Scheduling: campaigns → schedules (editor sheet, `?schedule=` deep link) → calendar; emergency broadcasts. */
const campaignsRoute = createRoute({ getParentRoute: () => appRoute, path: 'campaigns', component: CampaignsPage })
const campaignDetailRoute = createRoute({ getParentRoute: () => appRoute, path: 'campaigns/$campaignId', component: CampaignDetailPage })
const schedulesRoute = createRoute({ getParentRoute: () => appRoute, path: 'schedules', validateSearch: validateSchedulesSearch, component: SchedulesPage })
const calendarRoute = createRoute({ getParentRoute: () => appRoute, path: 'calendar', validateSearch: validateCalendarSearch, component: CalendarPage })
/** Phase D: rule library ("Conditions") used by campaigns, schedules and channel items. */
const rulesRoute = createRoute({ getParentRoute: () => appRoute, path: 'rules', component: RulesPage })
/** Phase F: Signetix Flow — list/dashboard (`?tab=webhooks`), builder (`/new?template=`, `/$flowId?tab=`). */
const flowsRoute = createRoute({ getParentRoute: () => appRoute, path: 'flows', validateSearch: validateFlowsSearch, component: FlowsPage })
const newFlowRoute = createRoute({ getParentRoute: () => appRoute, path: 'flows/new', validateSearch: validateNewFlowSearch, component: NewFlowPage })
const flowRoute = createRoute({ getParentRoute: () => appRoute, path: 'flows/$flowId', validateSearch: validateFlowSearch, component: FlowDetailPage })
const emergencyRoute = createRoute({ getParentRoute: () => appRoute, path: 'emergency', component: EmergencyPage })

/** QR payload target: `/pair?c=<code>&k=<nonce>`. Signed-out users bounce via /login and return here. */
const pairRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'pair',
  // `c` stays as parsed (the router reads `482731` as a number) so the URL isn't rewritten.
  validateSearch: (s: Record<string, unknown>): { c?: string | number; k?: string } => ({
    c: typeof s.c === 'string' || typeof s.c === 'number' ? s.c : undefined,
    k: str(s.k),
  }),
  component: PairPage,
})

const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'users',
  validateSearch: (s: Record<string, unknown>): { tab?: 'members' | 'invitations'; invite?: true } => ({
    tab: s.tab === 'invitations' || s.tab === 'members' ? s.tab : undefined,
    invite: bool(s.invite),
  }),
  component: UsersPage,
})

const rolesRoute = createRoute({ getParentRoute: () => appRoute, path: 'roles', component: RolesPage })
const newRoleRoute = createRoute({ getParentRoute: () => appRoute, path: 'roles/new', component: NewRolePage })
const roleRoute = createRoute({ getParentRoute: () => appRoute, path: 'roles/$roleId', component: EditRolePage })

const auditRoute = createRoute({ getParentRoute: () => appRoute, path: 'audit-log', component: AuditLogPage })

const settingsRoute = createRoute({ getParentRoute: () => appRoute, path: 'settings', component: SettingsLayout })
const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/settings/organization', replace: true })
  },
})
const settingsOrganizationRoute = createRoute({ getParentRoute: () => settingsRoute, path: 'organization', component: OrganizationSettings })
const settingsSubscriptionRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'subscription',
  validateSearch: (s: Record<string, unknown>): { change?: true } => ({ change: bool(s.change) }),
  component: SubscriptionSettings,
})
const settingsProfileRoute = createRoute({ getParentRoute: () => settingsRoute, path: 'profile', component: ProfileSettings })
const settingsSecurityRoute = createRoute({ getParentRoute: () => settingsRoute, path: 'security', component: SecuritySettings })
const settingsPlayerUpdatesRoute = createRoute({ getParentRoute: () => settingsRoute, path: 'player-updates', component: PlayerUpdateSettings })
// Phase 12: integrations & notifications
const settingsNotificationsRoute = createRoute({ getParentRoute: () => settingsRoute, path: 'notifications', component: NotificationSettings })
const settingsWebhooksRoute = createRoute({ getParentRoute: () => settingsRoute, path: 'webhooks', component: WebhooksSettings })
const settingsApiKeysRoute = createRoute({ getParentRoute: () => settingsRoute, path: 'api-keys', component: ApiKeysSettings })
const settingsBrandingRoute = createRoute({ getParentRoute: () => settingsRoute, path: 'branding', component: BrandingSettings })
const notificationsRoute = createRoute({ getParentRoute: () => appRoute, path: 'notifications', component: NotificationsPage })

/** Routes for features that ship in later phases. */
function upcoming<P extends string>(path: P) {
  return createRoute({ getParentRoute: () => appRoute, path, component: ComingSoonPage })
}

const upcomingRoutes = [
  upcoming('templates'),
] as const

/* --------------------------------------------------------- platform admin */

const adminRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'admin',
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions)
    if (!me?.user.is_platform_admin) throw redirect({ to: '/' })
  },
  component: AdminLayout,
})
const adminDashboardRoute = createRoute({ getParentRoute: () => adminRoute, path: '/', component: AdminDashboardPage })
const adminTenantsRoute = createRoute({ getParentRoute: () => adminRoute, path: 'tenants', component: AdminTenantsPage })
const adminTenantRoute = createRoute({ getParentRoute: () => adminRoute, path: 'tenants/$tenantId', component: AdminTenantDetailPage })
const adminPlansRoute = createRoute({ getParentRoute: () => adminRoute, path: 'plans', component: AdminPlansPage })
const adminAuditRoute = createRoute({ getParentRoute: () => adminRoute, path: 'audit-logs', component: AdminAuditLogsPage })
const adminReleasesRoute = createRoute({ getParentRoute: () => adminRoute, path: 'releases', component: AdminReleasesPage })
const adminReleaseRoute = createRoute({ getParentRoute: () => adminRoute, path: 'releases/$releaseId', component: AdminReleaseDetailPage })
const adminHealthRoute = createRoute({ getParentRoute: () => adminRoute, path: 'health', component: AdminHealthPage })
const adminSecurityRoute = createRoute({ getParentRoute: () => adminRoute, path: 'security', component: AdminSecurityPage })
const adminFailedJobsRoute = createRoute({ getParentRoute: () => adminRoute, path: 'failed-jobs', component: AdminFailedJobsPage })

/* --------------------------------------------------------------- the tree */

export const routeTree = rootRoute.addChildren([
  loginRoute,
  registerRoute,
  inviteRoute,
  authedRoute.addChildren([
    onboardingRoute,
    appRoute.addChildren([
      dashboardRoute,
      locationsRoute,
      locationDetailRoute,
      displaysRoute,
      displayDetailRoute,
      playersRoute,
      playerDetailRoute,
      groupsRoute,
      mediaRoute,
      slideshowsRoute,
      slideshowEditorRoute,
      slideshowPreviewRoute,
      channelsRoute,
      channelEditorRoute,
      dataSourcesRoute,
      dataSourceDetailRoute,
      variablesRoute,
      localContentRoute,
      slotApprovalsRoute,
      fieldsRoute,
      analyticsRoute,
      proofOfPlayRoute,
      campaignsRoute,
      campaignDetailRoute,
      schedulesRoute,
      calendarRoute,
      rulesRoute,
      flowsRoute,
      newFlowRoute,
      flowRoute,
      emergencyRoute,
      pairRoute,
      usersRoute,
      rolesRoute,
      newRoleRoute,
      roleRoute,
      auditRoute,
      settingsRoute.addChildren([settingsIndexRoute, settingsOrganizationRoute, settingsSubscriptionRoute, settingsProfileRoute, settingsSecurityRoute, settingsPlayerUpdatesRoute, settingsNotificationsRoute, settingsWebhooksRoute, settingsApiKeysRoute, settingsBrandingRoute]),
      notificationsRoute,
      ...upcomingRoutes,
    ]),
    adminRoute.addChildren([adminDashboardRoute, adminTenantsRoute, adminTenantRoute, adminPlansRoute, adminAuditRoute, adminReleasesRoute, adminReleaseRoute, adminHealthRoute, adminFailedJobsRoute, adminSecurityRoute]),
  ]),
])

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    // Let TanStack Query own caching; the router only orchestrates.
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
    defaultPendingMs: 300,
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}
